import { useHead } from '#imports'
import type { Ref } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { GetTechnicalSpecificationsResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/technical-specifications'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'

import type { GetShippingResponse } from '~/api/product-page/types'
import type { DisplayedReview } from '~/scopes/reviews/reviews-display/models/reviews'
import { useRegisterShoppingAssistantStructuredData } from '~/scopes/shopping-assistant/composables/useShoppingAssistantStructuredData'
import { prepareJsonLdScript } from '~/utils/seo/schema'

import { useProductHeadDescription } from './useProductHeadDescription'
import { useProductHeadJsonLd } from './useProductHeadJsonLd'
import { useProductHeadLink } from './useProductHeadLink'
import { useProductHeadMeta } from './useProductHeadMeta'
import { useProductHeadTitle } from './useProductHeadTitle'

export function useProductHead(
  productResponse: Ref<GetProductResponse | undefined>,
  selectedOfferResponse: Ref<GetPickersResponse['selectedOffer']>,
  ratesResponse: Ref<Rating | undefined>,
  reviewsResponse: Ref<{ results: DisplayedReview[] } | undefined>,
  technicalSpecificationsResponse: Ref<
    GetTechnicalSpecificationsResponse | undefined
  >,
  isOutOfStock: Ref<boolean>,
  shippingResponse: Ref<GetShippingResponse | null | undefined>,
  // Optional: only the modular PDP (via useSeo) injects the server-side subsidised price (PPI-7740).
  // Legacy product pages omit it and the JSON-LD keeps the non-subsidised price.
  priceForJsonLd?: Ref<Price | undefined>,
) {
  const { title } = useProductHeadTitle({ productResponse })
  const { description } = useProductHeadDescription({ productResponse })
  const { meta } = useProductHeadMeta({ productResponse, title, description })
  const { link } = useProductHeadLink({ productResponse })
  const { jsonLd } = useProductHeadJsonLd({
    description,
    productResponse,
    selectedOfferResponse,
    ratesResponse,
    reviewsResponse,
    technicalSpecificationsResponse,
    isOutOfStock,
    priceForJsonLd,
    shippingResponse,
  })
  // settles after this setup runs, so the head must re-resolve at render/SSR-finalization time
  // (not snapshot `jsonLd.value` once) for the subsidised price to land in the streamed HTML.
  //
  // The `key: 'pdp-jsonld'` on the script tag is critical: unhead renders it as a
  // `data-hid` attribute in SSR HTML, so the client can match the existing element by
  // identity (not by content) and update its innerHTML in-place. Without it, unhead
  // dedupes `<script>` tags by their full serialized JSON content — when a lazy data
  // source (reviews, technical specs) resolves on the client after SSR timed out, the
  // content changes, the dedupe key no longer matches, and the SSR element is orphaned
  // while a new one is appended → duplicated JSON-LD in the DOM (PPI-7984).
  useHead(() => ({
    title: title.value,
    link: link.value,
    meta: meta.value,
    script: jsonLd.value
      ? [{ ...prepareJsonLdScript(jsonLd.value), key: 'pdp-jsonld' }]
      : [],
  }))

  useRegisterShoppingAssistantStructuredData(jsonLd)
}
